import { clsx } from '@alexis/helpers/clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getIsRtlState } from '@redux/selectors';

import styles from './LogSliderWithDistribution.module.scss';

const SECTIONS_COUNT = 20;

interface LogSliderWithDistributionProps {
  className?: string;
  marks?: Array<number>;
  max?: number;
  min?: number;
  onChange?(value: number | Array<number>, activeThumb: number): void;
  onChangeCommitted?(value: number | Array<number>, activeThumb: number): void;
  points: Array<number>;
  step?: number | null;
  value?: number | Array<number>;
  dataPw?: string;
}

const LogSliderWithDistribution: React.FC<LogSliderWithDistributionProps> = ({
  className,
  max = 100,
  min = 0,
  onChange,
  onChangeCommitted,
  points,
  step = 1,
  value = 0,
  dataPw,
}): JSX.Element | null => {
  const containerDivRef = useRef<HTMLDivElement>(null);
  const touchId = useRef<number>();
  const previousIndex = useRef<number>();
  const moveCount = useRef<number>(0);
  const valuesRef = useRef<Array<number>>();
  const [width, setWidth] = useState(1);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const isRtl = useSelector(getIsRtlState);

  const [groupedSections, setGroupedSections] = useState<{ [key: number]: number }>();
  const [highestSectionCount, setHighestSectionCount] = useState<number>(0);

  const isRange = Array.isArray(value);

  const rangeDifference = max - min;

  useEffect(() => {
    setWidth(containerDivRef.current?.getBoundingClientRect().width || 1);
  }, []);

  let values = isRange ? value.slice().sort(asc) : [value];
  values = values.map((value) => clamp(value, min, max));
  valuesRef.current = values;

  const minLogPosition = reverseLogarithmic(values[0], min, max);
  const maxLogPosition = isRange ? reverseLogarithmic(values[1], min, max) : 0;
  const minThumbOffset = valueToPercent(minLogPosition, min, max);
  const maxThumbOffset = isRange ? valueToPercent(maxLogPosition, min, max) : 0;
  const trackBarPercent = maxThumbOffset - minThumbOffset;
  const offset = (max - min) / width;

  const minTranslateOffset = isRtl
    ? Math.min(0, (min - minLogPosition) / offset)
    : Math.max(0, (minLogPosition - min) / offset);
  const minTranslateX = isRtl
    ? Math.max(-width + 16, minTranslateOffset)
    : Math.min(width - 16, minTranslateOffset);

  const maxTranslateOffset = isRtl
    ? Math.max(0, (max - maxLogPosition) / offset)
    : Math.min(0, (maxLogPosition - max) / offset);
  const maxTranslateX = isRtl
    ? Math.min(width + 16, maxTranslateOffset)
    : Math.max(-width + 16, maxTranslateOffset);

  const handleChange = useCallback<(value: number | Array<number>, index: number) => void>(
    (value: number | Array<number>, index: number) => {
      setActiveIndex(index);

      if (!!onChange) {
        onChange(value, index);
      }
    },
    [values, onChange],
  );

  const handleChangeCommitted = useCallback<(value: number | Array<number>, index: number) => void>(
    (value: number | Array<number>, index: number) => {
      if (!!onChangeCommitted) {
        onChangeCommitted(value, index);
      }
    },
    [values, onChangeCommitted],
  );

  const handleMouseDown = useCallback<(event: React.MouseEvent<HTMLDivElement>) => void>(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button === 0) {
        const finger = trackFinger(event, touchId);

        if (!!finger && !!valuesRef.current) {
          const fingerNewValue = getFingerNewValue(finger, false, valuesRef.current, isRtl);

          handleChange(fingerNewValue.value, fingerNewValue.activeIndex);
        }

        moveCount.current = 0;

        const document = event.currentTarget.ownerDocument ?? window.document;
        document.addEventListener('mousemove', handleTouchMove);
        document.addEventListener('mouseup', handleTouchEnd);
      }
    },
    [handleChange],
  );

  const handleTouchStart = useCallback<(event: React.TouchEvent<HTMLDivElement>) => void>(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const touch = event.changedTouches[0];

      if (!!touch) {
        touchId.current = touch.identifier;
      }

      const finger = trackFinger(event, touchId);

      if (!!finger && !!valuesRef.current) {
        const fingerNewValue = getFingerNewValue(finger, false, valuesRef.current, isRtl);

        handleChange(fingerNewValue.value, fingerNewValue.activeIndex);
      }

      moveCount.current = 0;

      const document = event.currentTarget.ownerDocument ?? window.document;
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    },
    [handleChange],
  );

  const handleTouchMove = useCallback<(event: TouchEvent | MouseEvent) => void>(
    (event: TouchEvent | MouseEvent) => {
      event.preventDefault();
      const finger = trackFinger(event, touchId);

      moveCount.current += 1;

      if (!isDragging && moveCount.current > 2) {
        setIsDragging(true);
      }

      if (!!finger && !!valuesRef.current) {
        const fingerNewValue = getFingerNewValue(finger, true, valuesRef.current, isRtl);

        handleChange(fingerNewValue.value, fingerNewValue.activeIndex);
      }
    },
    [handleChange],
  );

  const handleTouchEnd = useCallback<(event: TouchEvent | MouseEvent) => void>(
    (event: TouchEvent | MouseEvent) => {
      const finger = trackFinger(event, touchId);

      setIsDragging(false);

      if (!!finger && !!valuesRef.current) {
        const fingerNewValue = getFingerNewValue(finger, true, valuesRef.current, isRtl);

        handleChangeCommitted(fingerNewValue.value, fingerNewValue.activeIndex);
      }

      touchId.current = undefined;

      stopListening();
    },
    [handleChangeCommitted],
  );

  const stopListening = useCallback<() => void>(() => {
    const document = containerDivRef.current!.ownerDocument ?? window.document;

    document.removeEventListener('mousemove', handleTouchMove);
    document.removeEventListener('mouseup', handleTouchEnd);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  }, [handleTouchMove, handleTouchEnd]);

  //#region Utility functions
  function valueToPercent(value: number, min: number, max: number): number {
    return ((value - min) * 100) / (max - min);
  }

  function asc(a: number, b: number): number {
    return a - b;
  }

  function getDecimalPrecision(value: number): number {
    // This handles the case when num is very small (0.00000001), js will turn this into 1e-8.
    // When num is bigger than 1 or less than -1 it won't get converted to this notation so it's fine.
    if (Math.abs(value) < 1) {
      const parts = value.toExponential().split('e-');
      const matissaDecimalPart = parts[0].split('.')[1];
      return (matissaDecimalPart ? matissaDecimalPart.length : 0) + parseInt(parts[1], 10);
    }

    const decimalPart = value.toString().split('.')[1];
    return decimalPart ? decimalPart.length : 0;
  }

  function roundValueToStep(value: number, step: number, min: number): number {
    const nearest = Math.round((value - min) / step) * step + min;
    return Number(nearest.toFixed(getDecimalPrecision(step)));
  }

  function clamp(value: number, min: number, max: number): number {
    if (value == null) {
      return min;
    }
    return Math.min(Math.max(min, value), max);
  }

  function findClosest(values: Array<number>, currentValue: number): number | undefined {
    if (values[0] === values[1] && currentValue < values[0]) {
      return 0;
    } else if (values[0] === values[1] && currentValue > values[0]) {
      return 1;
    } else {
      const { index: closestIndex } =
        values.reduce<{ distance: number; index: number } | null>(
          (acc, value: number, index: number) => {
            const distance = Math.abs(currentValue - value);

            if (acc === null || distance < acc.distance || distance === acc.distance) {
              return { distance, index };
            }

            return acc;
          },
          null,
        ) ?? {};

      return closestIndex;
    }
  }

  function trackFinger(
    event: TouchEvent | MouseEvent | React.TouchEvent | React.MouseEvent,
    touchId: React.RefObject<number | undefined>,
  ): { x: number; y: number } | null {
    // The event is TouchEvent
    if (touchId.current !== undefined && !!(event as TouchEvent).changedTouches) {
      const touchEvent = event as TouchEvent;

      for (let i = 0; i < touchEvent.changedTouches.length; i += 1) {
        const touch = touchEvent.changedTouches[i];

        if (touch.identifier === touchId.current) {
          return { x: touch.clientX, y: touch.clientY };
        }
      }
      return null;
    }

    // The event is MouseEvent
    return { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY };
  }

  function getFingerNewValue(
    finger: { x: number; y: number },
    isMoving: boolean,
    values: Array<number>,
    isRtl: boolean,
  ): { value: number | Array<number>; activeIndex: number } {
    const { left, right } = containerDivRef.current!.getBoundingClientRect();
    let newValue = isRtl
      ? logarithmic((right - finger.x) * offset + min, min, max)
      : logarithmic((finger.x - left) * offset + min, min, max);

    if (!!step) {
      newValue = roundValueToStep(newValue, step, min);
    }

    let activeIndex = 0;

    if (isRange) {
      if (!isMoving) {
        activeIndex = findClosest(values, newValue)!;
      } else {
        activeIndex = previousIndex.current!;
      }

      newValue = clamp(newValue, min, max);
      newValue = clamp(
        newValue,
        values[activeIndex - 1] || -Infinity,
        values[activeIndex + 1] || Infinity,
      );

      const cloneValues = values.slice();
      cloneValues[activeIndex] = newValue;

      if (!isMoving) {
        activeIndex = cloneValues.indexOf(newValue);
        previousIndex.current = activeIndex;
      }

      return { value: cloneValues, activeIndex };
    } else {
      newValue = clamp(newValue, min, max);

      return { value: newValue, activeIndex: 0 };
    }
  }
  function logarithmic(value: number, minRange: number, maxRange: number): number {
    const logMinRange = Math.log(minRange);
    const logMaxRange = Math.log(maxRange);

    const scale = (logMaxRange - logMinRange) / (maxRange - minRange);
    return Math.exp(logMinRange + scale * (value - minRange));
  }

  function reverseLogarithmic(value: number, minRange: number, maxRange: number): number {
    const logMinRange = Math.log(minRange);
    const logMaxRange = Math.log(maxRange);

    const scale = (logMaxRange - logMinRange) / (maxRange - minRange);

    return (Math.log(value) - logMinRange) / scale + minRange;
  }
  //#endregion

  useEffect(() => {
    stopListening();
  }, [stopListening]);

  // Generate number of bars in bar chart
  useEffect(() => {
    if (!!points && points.length > 0 && rangeDifference !== undefined) {
      const sortedAndRoundedPoints = points
        .sort((a, b) => {
          if (a > b) {
            return 1;
          }
          if (b > a) {
            return -1;
          }
          return 0;
        })
        .map((point, index) => {
          if (index === 0) {
            return Math.floor(point);
          } else if (index === points.length - 1) {
            return Math.ceil(point);
          } else {
            return Math.round(point);
          }
        });

      let highestSectionCount = 0;

      const groupedSections = sortedAndRoundedPoints.reduce(
        (accumulator: { [key: number]: number }, point: number) => {
          const pointValue = reverseLogarithmic(point, min, max);
          const sectionNo = Math.min(
            SECTIONS_COUNT - 1,
            Math.round((pointValue - min) / (rangeDifference / SECTIONS_COUNT)),
          );

          if (accumulator[sectionNo] !== undefined) {
            accumulator[sectionNo] = ++accumulator[sectionNo];
          } else {
            accumulator[sectionNo] = 1;
          }

          if (accumulator[sectionNo] > highestSectionCount) {
            highestSectionCount = accumulator[sectionNo];
          }

          return accumulator;
        },
        {},
      );

      setHighestSectionCount(highestSectionCount);
      setGroupedSections(groupedSections);
    }
  }, [min, max, points, rangeDifference]);

  return (
    <div
      data-pw={dataPw}
      ref={containerDivRef}
      className={clsx(styles.container, isRtl && styles.rtl, className)}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Section: Bar chart */}
      {!!groupedSections ? (
        <div className={styles.barChart}>
          {Object.entries(groupedSections).map((groupedSection) => {
            const sectionWidthPercentage = 100 / SECTIONS_COUNT;
            const x = sectionWidthPercentage * parseInt(groupedSection[0]);
            const width = `${sectionWidthPercentage * 0.905}%`;
            const height = `${(groupedSection[1] / highestSectionCount) * 100}%`;

            return (
              <div
                data-testid='bar'
                key={groupedSection[0]}
                className={clsx(styles.bar)}
                style={{ width, height, [`${isRtl ? 'right' : 'left'}`]: `${x}%` }}
              ></div>
            );
          })}
        </div>
      ) : null}

      <div className={clsx(styles.slider, isDragging && styles.isDragging)}>
        <div className={styles.railBar}></div>

        <div
          className={styles.trackBar}
          style={{
            [isRtl ? 'right' : 'left']: `${isRange ? minThumbOffset : 0}%`,
            width: `${isRange ? trackBarPercent : minThumbOffset}%`,
          }}
        ></div>

        {isRange ? (
          <>
            <div
              className={clsx(styles.thumb, activeIndex === 0 && styles.active)}
              style={{
                transform: `translateX(${minTranslateX}px)`,
                [isRtl ? 'right' : 'left']: '-16px',
              }}
            ></div>
            <div
              className={clsx(styles.thumb, activeIndex === 1 && styles.active)}
              style={{
                transform: `translateX(${maxTranslateX}px)`,
                [isRtl ? 'left' : 'right']: '-16px',
              }}
            ></div>
          </>
        ) : (
          <div
            className={styles.thumb}
            style={{ [isRtl ? 'right' : 'left']: `${minThumbOffset}%` }}
          ></div>
        )}
      </div>
    </div>
  );
};

export default LogSliderWithDistribution;
